import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Mail, Phone, Trash2, Pencil, Send, Copy, X, ShieldCheck } from "lucide-react";
import { MemberAccessDialog } from "@/components/MemberAccessDialog";

/* A number we can actually text: 10 digits, or 11 starting with a 1, or already +E.164. */
export const looksLikeMobile = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (String(raw).trim().startsWith("+")) return digits.length >= 8 && digits.length <= 15;
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

const ROLE_STYLES = {
  company_owner: "bg-royal text-white",
  company_admin: "bg-royal text-white",
  general_manager: "bg-royal/90 text-white",
  ops_manager: "bg-royal/80 text-white",
  property_manager: "bg-royal/70 text-white",
  team_lead: "bg-gold text-royal-deep",
  cleaner: "bg-gold text-royal-deep",
  vendor: "bg-gold/70 text-royal-deep",
  property_owner: "bg-royal/10 text-royal",
  client: "bg-royal/10 text-royal",
};

const PLACEHOLDER_EMAIL = /@(resend\.dev|example\.(com|org|net)|test\.com)$/i;

export default function Team() {
  const { user } = useAuth();
  const canInvite = can(user, "member:invite");
  const canEdit = can(user, "member:update");
  const canRemove = can(user, "member:remove");

  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [access, setAccess] = useState(null);
  // Individual access is the owner's, admins' and the general manager's call.
  const canSetAccess = canEdit
    && ["company_owner", "company_admin", "general_manager"].includes(user?.tenant_role);
  const [form, setForm] = useState({ name: "", email: "", phone: "", tenant_role: "cleaner" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setMembers(data));
    api.get("/orgs/roles").then(({ data }) => setRoles(data));
    if (canInvite) api.get("/invitations").then(({ data }) => setInvites(data));
  }, [canInvite]);

  useEffect(() => {
    load();
  }, [load]);

  const grantable = roles.filter((r) => r.can_grant);
  // Mirror the server's rank rule: you never see Edit / Access / Remove on someone above you.
  const rankOf = (role) => roles.find((r) => r.value === role)?.rank ?? 0;
  const canManage = (m) => m._id === user?._id
    || rankOf(user?.tenant_role) >= rankOf(m.role);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (form.phone && !looksLikeMobile(form.phone)) {
      toast.error("Enter a mobile number we can text, like (615) 555-0192");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invitations", form);
      toast.success(`Invitation sent to ${data.email}`);
      setForm({ name: "", email: "", phone: "", tenant_role: "cleaner" });
      setInviteOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invitation");
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not revoke invitation");
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/users/${editing._id}`, {
        name: editing.name,
        email: editing.email,
        phone: editing.phone || "",
        role: editing.role,
      });
      toast.success("Member updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update member");
    }
    setBusy(false);
  };

  const remove = async (target) => {
    setBusy(true);
    try {
      await api.delete(`/users/${target._id}`);
      toast.success(`${target.name} removed from this organization`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove member");
    }
    setBusy(false);
  };

  const groups = roles.map((r) => r.value).filter((role) => members.some((m) => m.role === role));

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">People</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Team & Access</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Everyone in {user?.active_organization?.name || "your organization"}, and what they're allowed to do.
          </p>
        </div>
        {canInvite && (
          <Button
            data-testid="invite-person-btn"
            onClick={() => setInviteOpen(true)}
            className="bg-royal hover:bg-royal-deep text-white gap-2 h-11"
          >
            <Plus className="h-4 w-4" /> Invite person
          </Button>
        )}
      </div>

      {canInvite && invites.length > 0 && (
        <section data-testid="pending-invites" className="rounded-lg border border-gold/40 bg-gold/5 p-5">
          <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Pending invitations</h2>
          <div className="mt-3 space-y-2.5">
            {invites.map((i) => (
              <div
                key={i._id}
                data-testid={`invite-row-${i._id}`}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5"
              >
                <p className="text-sm font-medium text-royal-deep truncate">{i.email}</p>
                <span className="rounded-full bg-royal/5 border border-royal/10 px-2 py-0.5 text-[11px] font-semibold text-royal">
                  {i.role_label}
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    data-testid={`copy-invite-${i._id}`}
                    variant="outline"
                    onClick={() => copyLink(i.invite_link)}
                    className="h-9 text-xs border-royal/30 text-royal hover:bg-royal/5 gap-1.5"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy link
                  </Button>
                  <Button
                    data-testid={`revoke-invite-${i._id}`}
                    variant="outline"
                    onClick={() => revoke(i._id)}
                    className="h-9 text-xs border-destructive/40 text-destructive hover:bg-destructive/5 gap-1.5"
                  >
                    <X className="h-3.5 w-3.5" /> Revoke
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {groups.map((role) => {
        const list = members.filter((m) => m.role === role);
        const roleLabel = list[0]?.role_label || role;
        return (
          <section key={role} data-testid={`team-group-${role}`}>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">{roleLabel}s</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((m) => (
                <div
                  key={m._id}
                  data-testid={`user-card-${m._id}`}
                  className="rounded-lg border border-border bg-white p-5 hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center font-display font-semibold ${
                        ROLE_STYLES[m.role] || "bg-royal/10 text-royal"
                      }`}
                    >
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-royal-deep truncate">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{m.role_label}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                    <p className="flex items-center gap-2 truncate">
                      <Mail className="h-3.5 w-3.5 text-royal" /> {m.email}
                    </p>
                    {m.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-royal" /> {m.phone}
                      </p>
                    )}
                  </div>
                  {m.role === "property_owner" && PLACEHOLDER_EMAIL.test(m.email || "") && (
                    <p
                      className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-800"
                      data-testid={`placeholder-email-warning-${m._id}`}
                    >
                      Placeholder inbox — urgent-issue alerts won't reach this owner. Add their real email.
                    </p>
                  )}
                  <div className="mt-4 flex gap-2">
                    {canEdit && canManage(m) && (
                      <Button
                        data-testid={`edit-user-${m._id}`}
                        variant="outline"
                        onClick={() => setEditing({ ...m })}
                        className="flex-1 min-h-[40px] text-xs border-royal/30 text-royal hover:bg-royal/5 gap-2"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    )}
                    {canSetAccess && m._id !== user?._id && canManage(m) && (
                      <Button
                        data-testid={`access-user-${m._id}`}
                        variant="outline"
                        onClick={() => setAccess(m)}
                        className="flex-1 min-h-[40px] text-xs border-gold/50 text-royal-deep hover:bg-gold/10 gap-2"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Access{(m.overrides?.allow?.length || m.overrides?.deny?.length) ? " •" : ""}
                      </Button>
                    )}
                    {canRemove && m._id !== user?._id && canManage(m) && (
                      <Button
                        data-testid={`delete-user-${m._id}`}
                        variant="outline"
                        onClick={() => setConfirm(m)}
                        className="flex-1 min-h-[40px] text-xs border-destructive/40 text-destructive hover:bg-destructive/5 gap-2"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      <MemberAccessDialog member={access} onClose={() => setAccess(null)} onSaved={load} />

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Invite someone to your organization</DialogTitle>
            <DialogDescription>
              They'll get an email with a secure link that adds them to this company — no new company is created.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendInvite} className="space-y-4" data-testid="invite-form-dialog">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                data-testid="invite-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name (optional)</Label>
              <Input
                data-testid="invite-name-field"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Mobile number {form.tenant_role === "property_owner" || form.tenant_role === "client"
                  ? "(optional)" : "— for job texts"}
              </Label>
              <Input
                data-testid="invite-phone-input"
                type="tel"
                inputMode="tel"
                placeholder="(615) 555-0192"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-1.5 h-11"
              />
              {form.phone && !looksLikeMobile(form.phone) && (
                <p className="mt-1 text-xs text-destructive" data-testid="invite-phone-error">
                  That doesn't look like a mobile number we can text.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
              <Select value={form.tenant_role} onValueChange={(v) => set("tenant_role", v)}>
                <SelectTrigger data-testid="invite-role-select" className="mt-1.5 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {grantable.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`invite-role-option-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="send-invite-btn"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-royal hover:bg-royal-deep text-white gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invitation
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Edit member</DialogTitle>
            <DialogDescription>
              Alerts and reports are sent to this email address, so make sure it's the one they actually read.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-user-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
              <Input
                data-testid="edit-user-name-input"
                required
                value={editing?.name || ""}
                onChange={(e) => setEditing((u) => ({ ...u, name: e.target.value }))}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                data-testid="edit-user-email-input"
                type="email"
                required
                value={editing?.email || ""}
                onChange={(e) => setEditing((u) => ({ ...u, email: e.target.value }))}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <Input
                data-testid="edit-user-phone-input"
                value={editing?.phone || ""}
                onChange={(e) => setEditing((u) => ({ ...u, phone: e.target.value }))}
                className="mt-1.5 h-11"
              />
            </div>
            {editing?._id !== user?._id && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                <Select value={editing?.role} onValueChange={(v) => setEditing((u) => ({ ...u, role: v }))}>
                  <SelectTrigger data-testid="edit-user-role-select" className="mt-1.5 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {grantable.map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`edit-role-option-${r.value}`}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              data-testid="save-edit-user-btn"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-royal hover:bg-royal-deep text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Remove {confirm?.name}?</DialogTitle>
            <DialogDescription>
              This removes them from this organization. Their properties become unassigned and their scheduled jobs go
              back to the unassigned queue — nothing else is deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              data-testid="cancel-delete-user-btn"
              variant="outline"
              onClick={() => setConfirm(null)}
              className="border-royal/30 text-royal hover:bg-royal/5"
            >
              Keep access
            </Button>
            <Button
              data-testid="confirm-delete-user-btn"
              disabled={busy}
              onClick={() => remove(confirm)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove from organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
